import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";

import Resident from "../models/Resident";
import { EmergencyTransportRole } from "./Facilities";

/**
 * ### 緊急搬送された患者 API 操作クラス
 */
export default class EmergencyTransportPatients {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET EmergencyTransportPatients
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex?: number; endIndex?: number; targetFacilityId?: number }): Promise<EmergencyTransportPatientsGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EmergencyTransportPatients, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<EmergencyTransportPatientsGetModel>(
      userStore,
      requestUrl,
      this.constructor.name,
      params,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET EmergencyTransportPatient (単一取得)
   */
  public async get(userStore: LoginUser, id: number, queryParams?: { targetFacilityId?: number }): Promise<EmergencyTransportPatientModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EmergencyTransportPatient, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<EmergencyTransportPatientModel>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET EmergencyTransportPatientsCount
   */
  public async getCount(userStore: LoginUser): Promise<EmergencyTransportPatientsGetCountModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EmergencyTransportPatients, formatParams);
    const response = await this.commonService.executeGet<EmergencyTransportPatientsGetCountModel>(
      userStore,
      `${requestUrl}/count`,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### POST EmergencyTransportPatient
   */
  public async post(userStore: LoginUser, requestBody: EmergencyTransportPatientPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EmergencyTransportPatients, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH EmergencyTransportPatientConfirmed
   */
  public async changeConfirmed(userStore: LoginUser, id: number, requestBody: EmergencyTransportPatientConfirmedModel) {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.EmergencyTransportPatientConfirmed, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface EmergencyTransportPatientsGetModel {
  /** 施設の役割 (SENDER / RECEIVER / BOTH) */
  responseType: EmergencyTransportRole;
  /** 送信した患者情報一覧 ("SENDER" / "BOTH" の場合のみ格納、それ以外は空配列) */
  sendPatients: EmergencyTransportPatientModel[];
  /** 受入対象の患者情報一覧 ("RECEIVER" / "BOTH" の場合のみ格納、それ以外は空配列) */
  receivePatients: EmergencyTransportPatientModel[];
}

export interface EmergencyTransportPatientModel {
  /** 送信元施設ID（介護施設） [PK] */
  facilityId: number;
  /** 施設内で一意のID (シーケンス採番) [SK] */
  id: number;
  /** 確認フラグ (true: 確認済み, false: 未確認) */
  confirmed?: boolean;

  /** 送信元施設名 */
  facilityName: string;
  /** 送信元施設住所 */
  facilityAddress: string;
  /** 送信元施設電話番号 */
  facilityTel: string;

  /** 送信先病院施設ID [GSI-PK] */
  targetFacilityId: number;
  /** 送信先病院名 */
  targetFacilityName: string;
  /** 送信先病院住所 */
  targetFacilityAddress: string;
  /** 送信先病院電話番号 */
  targetFacilityTel: string;

  /** 対象利用者ソートキー（送信された場合のみ保持） */
  residentSortKey?: string;
  /** 対象利用者氏名 */
  name: string;

  /** 当時の情報のスナップショット */
  patientSnapshotInfo?: Resident;

  /** 担当職員名 */
  assignedStaffName: string;
  /** 生年月日 (YYYYMMDDhhmmssSSS) */
  birthday?: number;
  /** 性別（男性、女性） */
  gender?: string;
  /** 身長（例：163.2） */
  height?: string;
  /** 体重（例：51.2） */
  weight?: string;
  /** 血液型（例：A） */
  bloodType?: string;
  /** 家族情報の配列 */
  families?: EmergencyTransportPatientFamily[];

  /** かかりつけ病院名 */
  primaryHospitalName?: string;
  /** かかりつけ担当医 */
  primaryDoctorName?: string;
  /** かかりつけ病院電話番号 */
  primaryHospitalTel?: string;

  /** 申し送り事項・搬送理由などの補足テキスト */
  note: string;

  /** 最高血圧（例：120） */
  maxBloodPressure?: number;
  /** 最低血圧（例：80） */
  minBloodPressure?: number;
  /** 脈拍（例：60） */
  pulse?: number;
  /** 呼吸数（例：60） */
  respiration?: number;
  /** 体温（例：36.5） */
  bodyTemperature?: string;
  /** サチュレーション (SpO2)（例：95） */
  spo2?: number;

  /** 病院側に伝えておきたいこと・特記事項 */
  specialNotes?: string;
  /** 禁忌・アレルギー情報 */
  allergiesAndContraindications?: string;
  /** 既往歴情報 */
  medicalHistory?: string;
  /** 服用中の薬情報 */
  medications?: string;
  /** その他の連絡事項 */
  otherInfo?: string;

  /** 送信操作を行ったユーザーID */
  userId: string;
  /** 送信操作を行ったユーザー名 */
  userName: string;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 作成日時 (YYYYMMDDhhmmssSSS) [GSI-SK] */
  created: number;
  /** 更新日時 (YYYYMMDDhhmmssSSS) */
  updated: number;
}

/**
 * ### 家族情報
 */
export interface EmergencyTransportPatientFamily {
  /** 家族名 */
  name: string;
  /** 連絡先電話番号 */
  tel?: string;
}

export interface EmergencyTransportPatientsGetCountModel {
  /** 施設の役割 (SENDER / RECEIVER / BOTH) */
  responseType: EmergencyTransportRole;
  /** 送信した患者件数 ("SENDER" / "BOTH" の場合のみカウント、それ以外は0) */
  sendCount: number;
  /** 受入対象の患者件数 ("RECEIVER" / "BOTH" の場合のみカウント、それ以外は0) */
  receiveCount: number;
}

export interface EmergencyTransportPatientPostModel {
  /** 送信先病院施設ID [GSI-PK] */
  targetFacilityId: number;

  /** 対象利用者ソートキー（送信された場合のみ保持） */
  residentSortKey?: string;
  /** 対象利用者氏名 */
  name: string;

  /** 担当職員名 */
  assignedStaffName: string;
  /** 生年月日 (YYYYMMDDhhmmssSSS) */
  birthday?: number;
  /** 性別（男性、女性） */
  gender?: string;
  /** 身長（例：163.2） */
  height?: string;
  /** 体重（例：51.2） */
  weight?: string;
  /** 血液型（例：A） */
  bloodType?: string;
  /** 家族情報の配列 */
  families?: EmergencyTransportPatientFamily[];

  /** かかりつけ病院名 */
  primaryHospitalName?: string;
  /** かかりつけ担当医 */
  primaryDoctorName?: string;
  /** かかりつけ病院電話番号 */
  primaryHospitalTel?: string;

  /** 申し送り事項・搬送理由などの補足テキスト */
  note: string;

  /** 最高血圧（例：120） */
  maxBloodPressure?: number;
  /** 最低血圧（例：80） */
  minBloodPressure?: number;
  /** 脈拍（例：60） */
  pulse?: number;
  /** 呼吸数（例：60） */
  respiration?: number;
  /** 体温（例：36.5） */
  bodyTemperature?: string;
  /** サチュレーション (SpO2)（例：95） */
  spo2?: number;

  /** 病院側に伝えておきたいこと・特記事項 */
  specialNotes?: string;
  /** 禁忌・アレルギー情報 */
  allergiesAndContraindications?: string;
  /** 既往歴情報 */
  medicalHistory?: string;
  /** 服用中の薬情報 */
  medications?: string;
  /** その他の連絡事項 */
  otherInfo?: string;
}

export interface EmergencyTransportPatientConfirmedModel {
  /** 送信元施設ID（介護施設） */
  sendFacilityId: number;
  /** 確認フラグ (true: 確認済み, false: 未確認) */
  confirmed: boolean;
  /** 整合性チェック用更新日時 (YYYYMMDDhhmmssSSS) */
  updated: number;
}
